/**
 * Motor de cálculo de preço — Frontend
 *
 * Versão simplificada do calculatePrice.ts das Cloud Functions.
 * Usa a mesma lógica de centavos pra evitar erros de ponto flutuante.
 *
 * Usado pra cálculo client-side de preços no catálogo e páginas de produto.
 * O cálculo REAL server-side é feito na CF submitQuoteRequest — este módulo
 * é só pra UX (mostrar preço enquanto o cliente ajusta quantidade).
 */

export interface PricingTier {
  minQty: number;
  maxQty: number | null;
  markupPercent: number;
}

export interface CostRangeMarkup {
  minCost: number;
  maxCost: number | null;
  markupPercent: number;
}

export interface CustomizationCost {
  silk_screen?: number;
  uv_print?: number;
  laser?: number;
}

export type PersonalizationType = "silk_screen" | "uv_print" | "laser";

export interface PriceCalculation {
  supplierCost: number;
  customizationCost: number;
  totalCost: number;
  markupPercent: number;
  unitPrice: number;
  quantity: number;
  subtotal: number;
  margin: number;
}

export interface PriceRange {
  min: number;
  max: number;
}

export type PriceCalculationError =
  | { code: "NO_TIERS"; message: string }
  | { code: "NO_APPLICABLE_TIER"; message: string }
  | { code: "INVALID_QUANTITY"; message: string }
  | { code: "INVALID_COST"; message: string }
  | { code: "INVALID_MARKUP"; message: string }
  | { code: "NO_VARIANT"; message: string }
  | { code: "PRODUCT_INACTIVE"; message: string }
  | { code: "PERSONALIZATION_TYPE_NOT_AVAILABLE"; message: string };

function toCents(value: number): number {
  return Math.round(value * 100);
}

function toReais(cents: number): number {
  return cents / 100;
}

function applyMarkup(costCents: number, markupPercent: number): number {
  return Math.round(costCents + (costCents * markupPercent) / 100);
}

export function findApplicableTier(
  tiers: readonly PricingTier[],
  quantity: number
): PricingTier | null {
  if (!Number.isInteger(quantity) || quantity <= 0) return null;

  let applicable: PricingTier | null = null;

  for (const tier of tiers) {
    if (quantity >= tier.minQty) {
      if (tier.maxQty === null || quantity <= tier.maxQty) {
        applicable = tier;
      }
    }
  }

  if (!applicable && tiers.length > 0) {
    const sorted = [...tiers].sort((a, b) => b.minQty - a.minQty);
    applicable = sorted[0]!;
  }

  return applicable;
}

export function findCostMarkup(
  markups: readonly CostRangeMarkup[],
  totalCost: number
): CostRangeMarkup | null {
  if (markups.length === 0) return null;

  const sorted = [...markups].sort((a, b) => a.minCost - b.minCost);

  for (const m of sorted) {
    if (totalCost >= m.minCost) {
      if (m.maxCost === null || totalCost <= m.maxCost) {
        return m;
      }
    }
  }

  return null;
}

export function calculatePriceForQuantity(
  supplierCost: number,
  customizationCost: number,
  tiers: readonly PricingTier[],
  quantity: number,
  costRangeMarkups?: readonly CostRangeMarkup[]
): PriceCalculation | PriceCalculationError {
  if (!Number.isInteger(quantity) || quantity <= 0) {
    return {
      code: "INVALID_QUANTITY",
      message: "Quantidade deve ser um inteiro positivo.",
    };
  }

  const tier = findApplicableTier(tiers, quantity);
  if (!tier) {
    return {
      code: "NO_APPLICABLE_TIER",
      message: `Nenhuma faixa de preço para quantidade ${quantity}.`,
    };
  }

  if (supplierCost < 0 || !Number.isFinite(supplierCost)) {
    return { code: "INVALID_COST", message: "Custo do fornecedor inválido." };
  }
  if (customizationCost < 0 || !Number.isFinite(customizationCost)) {
    return { code: "INVALID_COST", message: "Custo de personalização inválido." };
  }

  // Determina markup: custo-range tem prioridade sobre quantidade
  let markupPercent = tier.markupPercent;
  if (costRangeMarkups && costRangeMarkups.length > 0) {
    const totalCost = supplierCost + customizationCost;
    const costMarkup = findCostMarkup(costRangeMarkups, totalCost);
    if (costMarkup) {
      markupPercent = costMarkup.markupPercent;
    }
  }

  if (markupPercent <= 0 || !Number.isFinite(markupPercent)) {
    return { code: "INVALID_MARKUP", message: "Markup inválido." };
  }

  const supplierCents = toCents(supplierCost);
  const customCents = toCents(customizationCost);
  const totalCostCents = supplierCents + customCents;
  const unitPriceCents = applyMarkup(totalCostCents, markupPercent);

  const subtotalCents = unitPriceCents * quantity;

  return {
    supplierCost,
    customizationCost,
    totalCost: toReais(totalCostCents),
    markupPercent,
    unitPrice: toReais(unitPriceCents),
    quantity,
    subtotal: toReais(subtotalCents),
    margin: toReais(toReais(unitPriceCents - totalCostCents) * quantity),
  };
}

export function getPriceRange(
  supplierCost: number,
  customizationCost: number,
  tiers: readonly PricingTier[],
  costRangeMarkups?: readonly CostRangeMarkup[]
): PriceRange | null {
  // Se tem markups por custo, o preço é único
  if (costRangeMarkups && costRangeMarkups.length > 0) {
    const totalCost = supplierCost + customizationCost;
    const costMarkup = findCostMarkup(costRangeMarkups, totalCost);

    if (costMarkup) {
      const totalCostCents = toCents(supplierCost) + toCents(customizationCost);
      const unitPriceCents = applyMarkup(totalCostCents, costMarkup.markupPercent);
      const price = toReais(unitPriceCents);
      return { min: price, max: price };
    }
  }

  if (tiers.length === 0) return null;

  const sorted = [...tiers].sort((a, b) => a.markupPercent - b.markupPercent);

  const lowest = sorted[0];
  const highest = sorted[sorted.length - 1];

  if (!lowest || !highest) return null;

  const totalCostCents = toCents(supplierCost) + toCents(customizationCost);
  const minCents = applyMarkup(totalCostCents, lowest.markupPercent);
  const maxCents = applyMarkup(totalCostCents, highest.markupPercent);

  return {
    min: toReais(minCents),
    max: toReais(maxCents),
  };
}
